import { useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import {
  GOOGLE_FONT_FAMILIES,
  SYSTEM_FONT_OPTIONS,
  normalizeFontFamilyName,
} from './googleFonts'
import { loadGoogleFont } from './loadGoogleFont'
import { splitFontCss } from './splitBannerDefaults'

export default function FontFamilyPicker({
  value,
  disabled = false,
  onChange,
}) {
  const current = normalizeFontFamilyName(value)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    loadGoogleFont(current)
  }, [current])

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const google = GOOGLE_FONT_FAMILIES.filter((f) =>
      !q ? true : f.toLowerCase().includes(q)
    )
    const system = SYSTEM_FONT_OPTIONS.filter((o) =>
      !q
        ? true
        : o.label.toLowerCase().includes(q) || o.value.includes(q)
    )
    return { system, google: google.slice(0, 60) }
  }, [query])

  const select = (family) => {
    const next = normalizeFontFamilyName(family)
    loadGoogleFont(next)
    onChange?.(next)
    setOpen(false)
    setQuery('')
  }

  const applyCustom = () => {
    const q = query.trim()
    if (!q) return
    select(q)
  }

  return (
    <div className="font-family-picker" ref={rootRef}>
      <button
        type="button"
        className="font-family-picker-trigger"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{ fontFamily: splitFontCss(current) }}
      >
        {SYSTEM_FONT_OPTIONS.find((o) => o.value === current)?.label || current}
      </button>
      {open ? (
        <div className="font-family-picker-dropdown">
          <label className="font-family-picker-search">
            <Search size={14} />
            <input
              type="search"
              value={query}
              autoFocus
              placeholder="Pesquisar ou digitar nome da fonte..."
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyCustom()
                }
              }}
            />
          </label>
          {query.trim() ? (
            <button
              type="button"
              className="font-family-picker-custom"
              onClick={applyCustom}
            >
              Usar “{query.trim()}” (Google Fonts)
            </button>
          ) : null}
          <div className="font-family-picker-list">
            {filtered.system.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={
                  current === opt.value
                    ? 'font-family-picker-item is-active'
                    : 'font-family-picker-item'
                }
                style={{ fontFamily: splitFontCss(opt.value) }}
                onClick={() => select(opt.value)}
              >
                {opt.label}
              </button>
            ))}
            {filtered.google.map((family) => (
              <button
                key={family}
                type="button"
                className={
                  current === family
                    ? 'font-family-picker-item is-active'
                    : 'font-family-picker-item'
                }
                style={{ fontFamily: `"${family}", Georgia, serif` }}
                onMouseEnter={() => loadGoogleFont(family)}
                onFocus={() => loadGoogleFont(family)}
                onClick={() => select(family)}
              >
                {family}
              </button>
            ))}
            {filtered.system.length === 0 && filtered.google.length === 0 ? (
              <p className="font-family-picker-empty">
                Digite o nome exato e pressione Enter para carregar do Google Fonts.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
